import { Button, InputIcon } from '@platform/ui-library';
import { pick } from 'lodash';
import { FC, SyntheticEvent, useMemo } from 'react';
import { FormField, FocusGroup, Select } from '@app/form_components';
import {
  useDesign,
  useValueForm
} from '@app/hooks';
import {
  FormControlProps,
  ModuleOutput,
  ModuleOutputArgumentName,
  NonFalsy,
  Option,
  ValueOrVariableType,
  VariableReferenceScope,
  VariableType
} from '@app/types';
import { createUuid } from '@app/utilities';
import { VariableAutocomplete } from '../../shared';

type ArgumentOption = (
  Option<ModuleOutput['argumentName']>
  & { type: VariableType | null; }
);

type FormValues = (
  Omit<ModuleOutput, 'argumentName' | 'type' | 'varName'>
  & {
    argumentName: string | null;
    type: ModuleOutput['type'] | null;
    varName: ModuleOutput['varName'] | '';
  }
);

const defaultValues: FormValues = {
  argumentName: null,
  id: '',
  sensitiveData: false,
  type: null,
  varName: ''
};

export type ModuleOutputFormProps = (
  FormControlProps<ModuleOutput>
  & {
    /** Module to output variables from. When null, allow the user to type in argumentName..*/
    moduleId: string | null;
    onDelete: () => void;
  }
);

export const ModuleOutputForm: FC<ModuleOutputFormProps> = ({
  moduleId,
  name,
  onBlur,
  onChange,
  onDelete,
  onFocus,
  value
}) => {
  const labels = window.l.form;
  const serialized = useDesign();
  const savedValues = useMemo((): FormValues => {
    return { ...defaultValues, ...value };
  }, [value]);
  const {
    createSubmitHandler,
    getControlProps
  } = useValueForm<FormValues, ModuleOutput>({
    name,
    savedValues,
    transformValues: (values): ModuleOutput => {
      return {
        // NonFalsy values after validate runs.
        ...pick(values, [
          'sensitiveData',
          'type',
          'varName'
        ]) as NonFalsy<FormValues, 'type' | 'varName'>,
        argumentName: values.argumentName!.trim() as ModuleOutputArgumentName,
        id: values.id || createUuid()
      };
    },
    validate: ({
      argumentName,
      varName
    }) => {
      argumentName = argumentName?.trim() ?? '';
      const scopePrefix = `${VariableReferenceScope.module}.`;
      let argumentNameError: string | null = null;
      if (!argumentName) {
        argumentNameError = `"${labels.module_argument_name}" is required.`;
      } else if (/\s/.test(argumentName)) {
        argumentNameError = `"${labels.module_argument_name}" must not contain spaces.`;
      } else if (
        argumentName !== 'out'
        && !(
          argumentName.startsWith(scopePrefix)
          && argumentName.length > scopePrefix.length
        )
      ) {
        argumentNameError = `"${labels.module_argument_name}" must be "out" or "${scopePrefix}<variable name>".`;
      }

      return {
        argumentName: argumentNameError,
        varName: (varName.trim()) ? null : `"${labels.return_value_to}" is required.`
      };
    }
  });

  const argumentOptions = useMemo((): ArgumentOption[] | null => {
    if (!moduleId) {
      return null;
    }

    const appModule = serialized.components.modules.find((m) => m.id === moduleId);
    return [
      {
        label: '* Module output',
        type: null,
        value: 'out'
      },
      ...appModule?.localVariables?.map((localVariable) => {
        const { type, varName } = localVariable.props.config;
        return {
          label: varName,
          type,
          value: `${VariableReferenceScope.module}.${varName}`
        } as const;
      }) ?? []
    ];
  }, [moduleId, serialized]);

  const handleSubmit = createSubmitHandler((values) => onChange(values));
  const { error: argumentNameFieldError, ...argumentNameField } = getControlProps('argumentName');
  const varNameField = getControlProps('varName');
  const selectedArgument = (
    !argumentOptions
    || !argumentNameField.value?.includes('.')
  ) ? null : argumentOptions.find((o) => o.value === argumentNameField.value);

  return (
    <FocusGroup onBlur={onBlur} onFocus={onFocus}>
      <form
        key="form"
        className="flex-column flex-gap"
        onSubmit={handleSubmit}
      >
        {(argumentOptions) ? (
          <Select<FormValues['argumentName']>
            {...argumentNameField}
            label={labels.module_argument_name}
            onChange={(newValue) => {
              argumentNameField.onChange(newValue);
              varNameField.onChange('');
            }}
            options={argumentOptions}
            placeholder={labels.select_variable}
          />
        ) : (
          <FormField
            error={argumentNameFieldError}
            label={labels.module_argument_name}
          >
            <InputIcon
              {...argumentNameField}
              onChange={(e: SyntheticEvent<HTMLInputElement>) => {
                argumentNameField.onChange(e.currentTarget.value);
              }}
              value={argumentNameField.value ?? ''}
            />
          </FormField>
        )}
        <VariableAutocomplete
          {...varNameField}
          label={labels.return_value_to}
          onChange={(newValue, option) => {
            varNameField.onChange(newValue.varName);
            getControlProps('sensitiveData').onChange(option?.sensitiveData ?? false);
            getControlProps('type').onChange(option?.type ?? null);
          }}
          searchTypes={(!selectedArgument?.type) ? undefined : [selectedArgument.type]}
          value={{
            type: ValueOrVariableType.variable,
            varName: varNameField.value
          }}
        />
        {(value?.id) ? (
          <div className="flex-gap">
            <Button
              color='warning'
              icon='delete'
              onClick={onDelete}
              theme='outline'
            />
            <Button type="submit">
              {labels.save_output}
            </Button>
          </div>
        ) : (
          <Button type="submit">
            {labels.add_output}
          </Button>
        )}
      </form>
    </FocusGroup>
  );
};
